import React, { useEffect, useState } from "react";
import { dismissComplete, dismissHalfway, getPlanState, SERIF, SLOT_LABEL } from "./practiceApi";
import { CompleteNote } from "./CompleteNote";
import { RestartButton, RestartCard } from "./RestartPlan";
import { TogetherLine } from "../together/TogetherLine";
import { HalfwayNote } from "./HalfwayNote";
import { PracticePlayer } from "./PracticePlayer";
import { NightCreator } from "./NightCreator";
import { PlanControls } from "./PlanControls";
import { ReminderLine } from "./ReminderLine";
import { DayDot, SlotTick, DotLegend } from "./PracticeDots";

// The gentle 21-day card: Day X of 21, 21 gold dots, today's sessions. Continuity, never a streak.
export const PracticeCard = () => {
  const [p, setP] = useState(undefined);
  const [last, setLast] = useState(null);
  const load = () => getPlanState().then((d) => { setP(d.plan); setLast(d.last); }).catch(() => setP(null));
  useEffect(() => {
    load();
    window.addEventListener("soulumo:practice", load);
    return () => window.removeEventListener("soulumo:practice", load);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return last ? <RestartCard last={last} /> : null;
  const night = p.night_installation?.status === "ready" ? p.night_installation : null;
  return (
    <section className="rounded-3xl border border-[#d7bb7a]/25 bg-[#10213f] p-6" data-testid="practice-card">
      {p.welcome_back && <p className="mb-3 text-sm text-[#9fb39a]" data-testid="practice-welcome-back">Welcome back. Your practice is here whenever you're ready.</p>}
      <p className="text-[0.62rem] uppercase tracking-[0.3em] text-[#d7bb7a]">21-Day Practice · {p.journey?.name}</p>
      <h2 className="mt-2 text-3xl text-[#f3ecd9]" style={{ fontFamily: SERIF }} data-testid="practice-day">
        {p.finished ? "Your 21 days are complete" : p.starts_on ? "Your 21 days are almost here" : `Day ${p.day} of ${p.days}`}
      </h2>
      {p.paused && <p className="mt-1 text-sm text-[#9fb39a]" data-testid="practice-paused-note">Paused. Your day count is waiting for you, with no reminders.</p>}
      {p.starts_on && <p className="mt-1 text-sm text-[#cfc6ae]" data-testid="practice-starts-on">Begins on {new Date(`${p.starts_on}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</p>}
      {p.together && <TogetherLine t={p.together} doneToday={Object.values(p.today).some(Boolean)} />}
      <div className="mt-5 grid grid-cols-7 gap-3 justify-items-start max-w-sm" data-testid="practice-dots">{p.dots.map((d) => <DayDot key={d.day} d={d} today={!p.finished && !p.starts_on && d.day === p.day} />)}</div>
      {!p.starts_on && !p.finished && <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#cfc6ae]" data-testid="practice-today">
        <span>Today</span>
        {p.slots.map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5" data-testid={`practice-today-${s}`} data-done={p.today[s] ? "1" : "0"}>
            {SLOT_LABEL[s]}
            <SlotTick s={s} done={!!p.today[s]} />
          </span>
        ))}
      </div>}
      <div className="mt-5 space-y-4">
        {p.halfway && <HalfwayNote onDismiss={() => { dismissHalfway().catch(() => {}); setP({ ...p, halfway: false }); }} />}
        {p.complete_note && <CompleteNote onDismiss={() => { dismissComplete().catch(() => {}); setP({ ...p, complete_note: false }); }} />}
        {p.journey?.audio_url && <PracticePlayer kind="day" src={p.journey.audio_url} title={p.journey.name} onLogged={setP} />}
        {p.night && (night
          ? <PracticePlayer kind="night" src={night.audio_url} title={`Night Installation · ${night.label}`} onLogged={setP} />
          : p.together && !p.together.is_owner
            ? <p className="text-sm text-[#cfc6ae]" data-testid="practice-together-night-pending">The shared Night Installation will appear here once {p.together.owner_name || "the organiser"} has created it.</p>
            : <NightCreator journeyId={p.journey_id} compact />)}
        {p.today.night && <p className="text-xs text-[#9fb39a]" data-testid="practice-played-tonight">Played tonight ✓</p>}
      </div>
      {!p.finished && <ReminderLine p={p} onChange={setP} />}
      {p.finished
        ? <div className="mt-6 border-t border-[#2b3d5e] pt-5" data-testid="practice-finished-restart"><RestartButton prev={p} /></div>
        : <PlanControls p={p} onChange={(np) => (np ? setP(np) : load())} />}
      <DotLegend slots={p.slots} />
    </section>
  );
};
